import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Application deadline - March 31, 2025
  const deadline = new Date('2026-10-01T23:59:59');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date()?.getTime();
      const distance = deadline?.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [deadline]);

  const benefits = [
    {
      icon: "Award",
      title: "Industry Recognition",
      description: "Certificates recognized by leading companies"
    },
    {
      icon: "Users",
      title: "Expert Faculty",
      description: "Learn from qualified professionals"
    },
    {
      icon: "TrendingUp",
      title: "Career Growth",
      description: "Progressive career development "
    },
    {
      icon: "Heart",
      title: "Personal Attention",
      description: "Learner Centered Approach with small class sizes"
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-500 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/20 rounded-full -translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full translate-x-48 translate-y-48"></div>
        <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-white/15 rounded-full -translate-x-16 -translate-y-16"></div>
      </div>
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Ready to Transform Your Career?
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Join thousands of successful graduates who've built rewarding careers 
            through our practical, industry-focused technical education programs.
          </p>
        </div>

        {/* Countdown Timer */}
        <div className="bg-yellow-300 backdrop-blur-m rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-2xl text-black font-bold mb-2">2ND GRADUATION APPROACHING</h3>
            <p className="text-black/80">KARUMO TTI INVITES YOU TO THE 2ND GRADUATION CELEBRATING 10 YEARS OF SERVICE</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Days', value: timeLeft?.days },
              { label: 'Hours', value: timeLeft?.hours },
              { label: 'Minutes', value: timeLeft?.minutes },
              { label: 'Seconds', value: timeLeft?.seconds }
            ]?.map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-black/20 rounded-xl p-4 mb-2">
                  <div className="text-3xl md:text-4xl font-bold">
                    {item?.value?.toString()?.padStart(2, '0')}
                  </div>
                </div>
                <div className="text-2xl text-black/80 font-medium">
                  {item?.label}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-black/80 mb-4">
              Do not miss the opportunity. Limited Seats Available
            </p>
            <Link to="/contact">
              <Button variant="secondary" size="lg" iconName="UserPlus" iconPosition="left">
                Apply Now - Secure Your Future
              </Button>
            </Link>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {benefits?.map((benefit, index) => (
            <div
              key={index}
              className="text-center group"
            >
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-white/30 transition-colors duration-300">
                <Icon name={benefit?.icon} size={32} color="white" />
              </div>
              <h4 className="text-lg font-bold mb-2">{benefit?.title}</h4>
              <p className="text-black/80 text-lg">{benefit?.description}</p>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto">
            <Link to="/contact" className="flex-1 sm:flex-none">
              <Button variant="secondary" size="lg" iconName="Send" iconPosition="left" fullWidth>
                Get Information Package
              </Button>
            </Link>
            
            <Link to="/about" className="flex-1 sm:flex-none">
              <Button variant="outline" size="lg" iconName="MapPin" iconPosition="left" fullWidth>
                Schedule Campus Visit
              </Button>
            </Link>
            
            <Link to="/contact" className="flex-1 sm:flex-none">
              <Button variant="ghost" size="lg" iconName="Phone" iconPosition="left" fullWidth>
                Call: +254(0)705636443
              </Button>
            </Link>
          </div>

          <div className="mt-8 text-center">
            <p className="text-white/80 text-lg mb-2">
              Have questions? Our admission's office is here to help
            </p>
            <div className="flex justify-center items-center space-x-6 text-lg">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>Mon-Fri: 8AM-5PM</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} />
                <span>admissions@karumotti.ac.ke</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;